type UpdatePreparation = () => boolean | Promise<boolean>
type UpdateGuard = () => boolean | Promise<boolean>

export interface UpdatePreparationResult {
  safe: boolean
  reason?: string
}

const preparations = new Set<UpdatePreparation>()
const guards = new Set<UpdateGuard>()
let activeOperations = 0

export function beginCriticalOperation(): () => void {
  activeOperations += 1
  let finished = false
  return () => {
    if (finished) return
    finished = true
    activeOperations = Math.max(0, activeOperations - 1)
  }
}

export async function runCriticalOperation<T>(operation: () => Promise<T>): Promise<T> {
  const finish = beginCriticalOperation()
  try {
    return await operation()
  } finally {
    finish()
  }
}

export function registerUpdatePreparation(preparation: UpdatePreparation): () => void {
  preparations.add(preparation)
  return () => preparations.delete(preparation)
}

export function registerUpdateGuard(guard: UpdateGuard): () => void {
  guards.add(guard)
  return () => guards.delete(guard)
}

export async function prepareForAppUpdate(): Promise<UpdatePreparationResult> {
  try {
    for (const preparation of preparations) {
      if (!(await preparation())) {
        return {
          safe: false,
          reason: 'Há alterações que ainda não foram salvas no banco local.',
        }
      }
    }

    if (activeOperations > 0) {
      return {
        safe: false,
        reason: 'Há uma gravação, exportação ou restauração em andamento.',
      }
    }

    for (const guard of guards) {
      if (!(await guard())) {
        return {
          safe: false,
          reason: 'Há um formulário com alterações não salvas.',
        }
      }
    }
    return { safe: true }
  } catch {
    return {
      safe: false,
      reason: 'Não foi possível confirmar o salvamento com segurança.',
    }
  }
}
